import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Toaster } from './components/ui/sonner';
import PasswordGate from './components/PasswordGate';
import HeaderSticky from './components/HeaderSticky';
import Hero from './components/Hero';
import OpportunityMetrics from './components/OpportunityMetrics';
import PhaseSection from './components/PhaseSection';
import WhyNowSection from './components/WhyNowSection';
import FinancialCharts from './components/FinancialCharts';
import TeamGrid from './components/TeamGrid';
import InvestorInterestForm from './components/InvestorInterestForm';
import FooterMinimal from './components/FooterMinimal';
import './App.css';

function App() {
  const [activeSection, setActiveSection] = useState('top');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['top', 'vision', 'opportunity', 'room', 'relic', 'ai', 'financials', 'team', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <PasswordGate>
      <div className="App">
        <HeaderSticky activeSection={activeSection} />
        <main>
        <Hero />
        <OpportunityMetrics />
        <PhaseSection
          id="room"
          phase="Phase 1"
          title="The Room"
          subtitle="Event-Driven Network Building — May 2026 Launch"
          description="We're not starting from zero. Industry leaders have already committed. May 2026, Las Vegas — 300 curated participants paying $2,000–$2,500 each. This is network-building that generates immediate revenue while establishing the trust layer."
          features={[
            "Precious Metals & Numismatics — May 2026, Las Vegas (Founding Event)",
            "Sports Cards & Memorabilia (Late 2026)",
            "Fine Watches (2027)",
            "Rare Collectibles (Following)"
          ]}
          metrics={[
            { label: "Ticket Price", value: "$2,000–$2,500" },
            { label: "Curated Attendees", value: "300" },
            { label: "Year 1 Revenue", value: "$2.5M–$4M" },
            { label: "Events Annually", value: "2–3" }
          ]}
          imageUrl="https://images.unsplash.com/photo-1649369341509-35dc4ccf95ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxyZWQlMjBjYXJwZXQlMjBjbG9zZWQlMjBkb29yJTIwZXhjbHVzaXZlJTIwVklQJTIwZW50cmFuY2UlMjBsdXh1cnl8ZW58MHx8fHwxNzcxNTYyMTQ4fDA&ixlib=rb-4.1.0&q=85"
          externalLink="https://sovereigngroup.io/theroom"
          showTractionBadge={true}
        />
        <PhaseSection
          id="relic"
          phase="Phase 2"
          title="Relic"
          subtitle="Blockchain-Backed Live Selling Platform"
          description="Once the network is established, launch the transactional layer. Immutable ownership transfer, verified provenance, instant settlement — all built on the trust foundation of The Room."
          features={[
            "Immutable ownership transfer",
            "Verified provenance tracking",
            "Instant settlement",
            "Live auction capabilities"
          ]}
          metrics={[
            { label: "Year 2 GMV", value: "$150M–$300M" },
            { label: "Year 2 Revenue", value: "$10M–$20M" },
            { label: "Year 3 GMV", value: "$500M–$1B" },
            { label: "Year 3 Revenue", value: "$30M–$70M" }
          ]}
          imageUrl="https://images.pexels.com/photos/29176372/pexels-photo-29176372.jpeg?auto=compress&cs=tinysrgb&w=800"
          reverse
        />
        <PhaseSection
          id="ai"
          phase="AI Layer"
          title="Intelligence-Driven Trust"
          subtitle="Authentication & Valuation at Speed"
          description="Real-time authentication assistance, valuation intelligence, and liquidity signaling. When high-value assets surface, qualified buyers are alerted instantly, and offers are accepted in real time."
          features={[
            "AI-powered authentication assistance",
            "Real-time valuation intelligence",
            "Mid-stream liquidity signals",
            "Instant buyer matching"
          ]}
          metrics={[
            { label: "Authentication Speed", value: "Real-time" },
            { label: "Valuation Accuracy", value: "Market-driven" },
            { label: "Liquidity Match", value: "Instant" },
            { label: "Trust Enhancement", value: "Continuous" }
          ]}
          imageUrl="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80"
        />
        <WhyNowSection />
        <FinancialCharts />
        <TeamGrid />
        <InvestorInterestForm />
      </main>
      <FooterMinimal />
      <Toaster />
    </div>
    </PasswordGate>
  );
}

export default App;
